import { uploadFiles, useToast } from "@qw/ui";
import { useCallback, useRef } from "react";
import { type DropzoneState, type FileRejection, useDropzone } from "react-dropzone";
import { type UseFormReturn, useFieldArray } from "react-hook-form";

import { CHAT_FILE_ACCEPT, CHAT_FILE_MAX_SIZE } from "../chat.constants";
import type { ChatFormValues } from "./chat-form.types";

export interface UseChatFilesDropzoneOptions {
  formMethods: UseFormReturn<ChatFormValues>;
  disabled: boolean;
}

export interface UseChatFilesDropzoneReturn {
  dropzone: DropzoneState;
}

export const useChatFilesDropzone = ({
  formMethods,
  disabled,
}: UseChatFilesDropzoneOptions): UseChatFilesDropzoneReturn => {
  const { toast } = useToast();
  const { append, update, remove } = useFieldArray({ control: formMethods.control, name: "files" });

  // Serialize form updates to prevent race conditions when multiple uploads complete simultaneously.
  // Without this, concurrent read-modify-write operations can cause uploaded files to be lost.
  const updateQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Helper to queue form state modifications and ensure they run sequentially
  const enqueueUpdate = useCallback((operation: () => void) => {
    updateQueueRef.current = updateQueueRef.current.then(operation);
  }, []);

  // Helper to find and operate on a file by its temp ID
  const findFileIndex = useCallback(
    (tempId: string) => formMethods.getValues("files").findIndex((file) => file.id === tempId),
    [formMethods]
  );

  const handleAcceptedFiles = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        return;
      }

      const failedUploads: Array<{ filename: string; error: unknown }> = [];

      await Promise.all(
        acceptedFiles.map(async (file) => {
          const tempId = `temp_${crypto.randomUUID()}`;

          // Add temp item immediately to show upload in progress
          append({ id: tempId, filename: file.name, mimeType: file.type, size: file.size });

          try {
            const { fileData } = await uploadFiles([file]);
            const uploaded = fileData[0];

            if (!uploaded) {
              throw new Error("Upload succeeded but no file data was returned");
            }

            // Queue the update to serialize form state modifications
            enqueueUpdate(() => {
              const index = findFileIndex(tempId);
              if (index !== -1) {
                update(index, {
                  id: uploaded.id,
                  filename: uploaded.filename,
                  mimeType: uploaded.mimeType,
                  size: uploaded.size,
                });
              }
            });
          } catch (error) {
            // Queue the removal to serialize form state modifications
            enqueueUpdate(() => {
              const index = findFileIndex(tempId);
              if (index !== -1) {
                remove(index);
              }
            });

            failedUploads.push({ filename: file.name, error });
          }
        })
      );

      // Wait for all queued updates to complete before showing error toast
      await updateQueueRef.current;

      if (failedUploads.length > 0) {
        const isSingle = failedUploads.length === 1;
        toast({
          variant: "destructive",
          title: isSingle
            ? `Failed to upload ${failedUploads[0].filename}`
            : `Failed to upload ${failedUploads.length} files`,
          description: isSingle
            ? failedUploads[0].error instanceof Error
              ? failedUploads[0].error.message
              : "Please try again"
            : failedUploads.map((f) => f.filename).join(", "),
        });
      }
    },
    [toast, append, update, remove, enqueueUpdate, findFileIndex]
  );

  const handleRejectedFiles = useCallback(
    (fileRejections: FileRejection[]) => {
      const combinedErrors = new Set<string>();
      for (const fileRejection of fileRejections) {
        for (const error of fileRejection.errors) {
          combinedErrors.add(error.message);
        }
      }

      const title = `${fileRejections.length} file${fileRejections.length > 1 ? "s" : ""} rejected`;
      const description = combinedErrors.size > 0 ? `${[...combinedErrors].join(". ")}.` : "An unknown error occurred.";

      toast({ variant: "destructive", title, description });
    },
    [toast]
  );

  const handleDropFiles = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        handleRejectedFiles(fileRejections);
      }

      await handleAcceptedFiles(acceptedFiles);
    },
    [handleAcceptedFiles, handleRejectedFiles]
  );

  const dropzone = useDropzone({
    accept: CHAT_FILE_ACCEPT,
    maxSize: CHAT_FILE_MAX_SIZE,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDragEnter: undefined,
    onDragLeave: undefined,
    onDragOver: undefined,
    onDrop: handleDropFiles,
    disabled,
  });

  return { dropzone };
};
